import axios, { AxiosError } from "axios";
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "react-query";
import {
  ISegmentationBlockListResponse,
  ISegmentationBlock,
  ICreateSegmentationBlockRequest,
  IUpdateSegmentationBlockRequest,
} from "../../@types/segmentationBlocks";

// Request parameters for the API call
const SEGMENTATION_BLOCKS_LIST_PARAMS = {
  feature_type: "EWA_BLOCKING",
  include_match_count: "true",
};

// GET /segment-assignments/?feature_type=EWA_BLOCKING - List blocks
export const SegmentationBlocksListApiService =
  async (): Promise<ISegmentationBlockListResponse> => {
    const url = "/segment-assignments/";
    const { data } = await axios.get(url, {
      params: SEGMENTATION_BLOCKS_LIST_PARAMS,
    });
    return data;
  };

export const useSegmentationBlocksListApiService = (): UseQueryResult<
  ISegmentationBlockListResponse,
  AxiosError
> =>
  useQuery<ISegmentationBlockListResponse, AxiosError>(
    [
      "SegmentationBlocksListApiService",
      ...Object.values(SEGMENTATION_BLOCKS_LIST_PARAMS),
    ],
    () => SegmentationBlocksListApiService(),
    { keepPreviousData: true }
  );

// POST /segment-assignments/ - Create block
export const CreateSegmentationBlockApiService = async (
  request: ICreateSegmentationBlockRequest
): Promise<ISegmentationBlock> => {
  const url = "/segment-assignments/";
  const { data } = await axios.post(url, request);
  return data;
};

export const useCreateSegmentationBlockApiService = (): UseMutationResult<
  ISegmentationBlock,
  AxiosError,
  ICreateSegmentationBlockRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<
    ISegmentationBlock,
    AxiosError,
    ICreateSegmentationBlockRequest
  >(CreateSegmentationBlockApiService, {
    mutationKey: ["CreateSegmentationBlockApiService"],
    onSuccess: () => {
      queryClient.invalidateQueries(["SegmentationBlocksListApiService"]);
    },
  });
};

// PATCH /segment-assignments/{id}/ - Update block (activate/deactivate)
export const UpdateSegmentationBlockApiService = async (
  request: IUpdateSegmentationBlockRequest
): Promise<ISegmentationBlock> => {
  const url = `/segment-assignments/${request.id}/`;
  const { data } = await axios.patch(url, { is_active: request.is_active });
  return data;
};

export const useUpdateSegmentationBlockApiService = (): UseMutationResult<
  ISegmentationBlock,
  AxiosError,
  IUpdateSegmentationBlockRequest
> => {
  const queryClient = useQueryClient();
  return useMutation<
    ISegmentationBlock,
    AxiosError,
    IUpdateSegmentationBlockRequest
  >(UpdateSegmentationBlockApiService, {
    mutationKey: ["UpdateSegmentationBlockApiService"],
    onSuccess: () => {
      queryClient.invalidateQueries(["SegmentationBlocksListApiService"]);
    },
  });
};
